/**
 * NotificationsBell — campana con dropdown de notificaciones in-app.
 *
 * - Polling cada 60s al contador.
 * - Al abrir el dropdown, carga las notif del user (no leídas primero).
 * - Click en una notif → navega al link + marca como leída automáticamente.
 * - Botón "X" por item: marca leída sin navegar (descartar silencioso).
 * - "Marcar todas leídas" en el header del dropdown.
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X, CheckCheck, DollarSign, CreditCard } from 'lucide-react';
import { notificationsApi } from '../utils/api';
import useAuthStore from '../store/authStore';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';

dayjs.extend(relativeTime);
dayjs.locale('es');

const TIPO_CFG = {
  cobro_cxc: { icon: DollarSign, color: 'text-emerald-600', bg: 'bg-emerald-100' },
  pago_cxp:  { icon: CreditCard, color: 'text-blue-600',    bg: 'bg-blue-100' }
};

export default function NotificationsBell() {
  const navigate = useNavigate();
  const { emisores, currentEmisor, setCurrentEmisor } = useAuthStore();
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Polling del contador cada 60s
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const res = await notificationsApi.unreadCount();
        setCount(res.data?.data?.count || 0);
      } catch { /* ignore */ }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, []);

  // Cerrar al click afuera
  useEffect(() => {
    const onClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const openDropdown = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const res = await notificationsApi.list({ limit: 50 });
      setItems(res.data?.data || []);
    } catch (e) {
      console.error('[NotifBell] list error:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleClickItem = async (item) => {
    setOpen(false);
    if (!item.leida) {
      try {
        await notificationsApi.markRead(item.id);
        setCount(c => Math.max(0, c - 1));
      } catch { /* ignore */ }
    }
    // Si la notificación es de otra empresa, cambiar al emisor correcto antes de navegar
    // (si no, la pantalla de Histórico carga los datos del emisor actual y no encuentra el cobro/pago).
    if (item.emisor_id && currentEmisor && String(item.emisor_id) !== String(currentEmisor.id)) {
      const destino = (emisores || []).find(e => String(e.id) === String(item.emisor_id));
      if (destino) {
        try { await setCurrentEmisor(destino); } catch { /* ignore */ }
      }
    }
    if (item.link) navigate(item.link);
  };

  const handleDismiss = async (e, item) => {
    e.stopPropagation();
    if (item.leida) return;
    try {
      await notificationsApi.markRead(item.id);
      setItems(prev => prev.map(i => i.id === item.id ? { ...i, leida: true, leida_at: new Date().toISOString() } : i));
      setCount(c => Math.max(0, c - 1));
    } catch { /* ignore */ }
  };

  const handleMarkAll = async () => {
    try {
      const res = await notificationsApi.markAllRead();
      const updated = res.data?.data?.updated || 0;
      setItems(prev => prev.map(i => ({ ...i, leida: true, leida_at: new Date().toISOString() })));
      setCount(0);
      if (updated > 0) console.log(`[NotifBell] ${updated} marcadas leídas`);
    } catch (e) { console.error(e); }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={open ? () => setOpen(false) : openDropdown}
        className={`relative p-2 rounded-lg transition-colors ${
          count > 0 ? 'hover:bg-amber-100 bg-amber-50' : 'hover:bg-gray-100'
        }`}
        title={count > 0 ? `${count} notificación(es) sin leer` : 'Notificaciones'}
      >
        {/* Halo pulsante alrededor del botón cuando hay notif sin leer */}
        {count > 0 && (
          <span className="absolute inset-0 rounded-lg ring-2 ring-amber-400/60 animate-ping-slow pointer-events-none" />
        )}
        {/* Campana — color ámbar + swing cuando hay notif sin leer */}
        <Bell
          className={`h-5 w-5 transition-colors relative ${
            count > 0 ? 'text-amber-600 animate-bell-swing' : 'text-gray-600'
          }`}
          style={{ transformOrigin: '50% 0%' }}
        />
        {/* Badge contador con pulso */}
        {count > 0 && (
          <>
            {/* Anillo expansivo detrás del badge */}
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-red-500 animate-ping opacity-75 pointer-events-none" />
            {/* Badge sólido encima */}
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 shadow-lg ring-2 ring-white">
              {count > 99 ? '99+' : count}
            </span>
          </>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-[420px] max-h-[500px] bg-white rounded-xl shadow-2xl border border-gray-200 z-50 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-gray-600" />
              <span className="text-sm font-semibold text-gray-900">Notificaciones</span>
              {count > 0 && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">{count}</span>}
            </div>
            {items.some(i => !i.leida) && (
              <button onClick={handleMarkAll} className="text-xs text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1">
                <CheckCheck className="h-3.5 w-3.5" />
                Marcar todas leídas
              </button>
            )}
          </div>

          {/* Lista */}
          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="p-8 text-center text-sm text-gray-500">Cargando...</div>
            ) : items.length === 0 ? (
              <div className="p-8 text-center text-sm text-gray-500">
                <Bell className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                Sin notificaciones
              </div>
            ) : (
              items.map(item => {
                const cfg = TIPO_CFG[item.tipo] || { icon: Bell, color: 'text-gray-600', bg: 'bg-gray-100' };
                const Icon = cfg.icon;
                return (
                  <div
                    key={item.id}
                    onClick={() => handleClickItem(item)}
                    className={`flex items-start gap-3 px-4 py-3 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors ${!item.leida ? 'bg-blue-50/40' : ''}`}
                  >
                    <div className={`p-2 rounded-lg ${cfg.bg} shrink-0`}>
                      <Icon className={`h-4 w-4 ${cfg.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${!item.leida ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>{item.titulo}</p>
                      {item.descripcion && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{item.descripcion}</p>}
                      <p className="text-[11px] text-gray-400 mt-1">{dayjs(item.created_at).fromNow()}</p>
                    </div>
                    {!item.leida && (
                      <button
                        onClick={(e) => handleDismiss(e, item)}
                        className="p-1 text-gray-400 hover:text-gray-700 rounded shrink-0"
                        title="Marcar leída sin abrir"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
